import * as Models from "app/services/proxy/model/models";
import {Lookup, UtilsService} from "framework/services/utils.service";
import {ControlOption} from "framework/ui/control-option";
import {TitleCaseFormatPipe, UnderscoresForSpacesPipe} from "framework/ui/formatting/string-format.pipe";

export enum ClusterType
{
    Amazon_EKS = "Amazon_EKS",
    Azure_AKS  = "Azure_AKS",
    GCP_GKE    = "GCP_GKE"
}

export enum KubernetesWorkloadType
{
    Deployment  = "deployment",
    DaemonSet   = "daemonset",
    StatefulSet = "statefulset",
    Replicaset  = "replicaset",
    Job         = "job",
    CronJob     = "cronjob",
}

export const ClusterNativeDetailsMap: Lookup<Models.ServiceAccountNativeDetailsBase, ClusterType> = {
    [ClusterType.Amazon_EKS]: new Models.ServiceAccountNativeDetailsForAwsEksCluster(),
    [ClusterType.Azure_AKS] : new Models.ServiceAccountNativeDetailsForAzureAksCluster(),
    [ClusterType.GCP_GKE]   : new Models.ServiceAccountNativeDetailsForGcpGkeCluster()
};

export const NativeDetailsClusterMap: Lookup<ClusterType, string> = {
    "ServiceAccountNativeDetailsForAwsEksCluster"  : ClusterType.Amazon_EKS,
    "ServiceAccountNativeDetailsForAzureAksCluster": ClusterType.Azure_AKS,
    "ServiceAccountNativeDetailsForGcpGkeCluster"  : ClusterType.GCP_GKE
};

export const KubernetesResourceLabelMap: Lookup<string, Models.KubernetesResourceType> = {
    [Models.KubernetesResourceType.compute]      : "Compute",
    [Models.KubernetesResourceType.control_plane]: "Control plane",
    [Models.KubernetesResourceType.cpu]          : "CPU",
    [Models.KubernetesResourceType.gpu]          : "GPU",
    [Models.KubernetesResourceType.memory]       : "Memory",
    [Models.KubernetesResourceType.network]      : "Network",
    [Models.KubernetesResourceType.storage]      : "Storage",
    [Models.KubernetesResourceType.unallocated]  : "Unallocated"
};

/** Platform widgets default to Kubernetes when no platform is stored on the model, so an unset
 * platform must never be treated as AI. */
export function isAiPlatform(platform: Models.Platforms): boolean
{
    return platform === Models.Platforms.ai;
}

export function resolvePlatformType(platform: Models.Platforms, isFinOpsForAiEnabled: boolean): Models.Platforms
{
    return isFinOpsForAiEnabled || !isAiPlatform(platform) ? platform : Models.Platforms.kubernetes;
}

export function resolvePlatformTypes(platforms: Models.Platforms[] | undefined | null,
                                     isFinOpsForAiEnabled: boolean): Models.Platforms[] | undefined | null
{
    if (!platforms) return platforms;
    return Array.from(new Set(platforms.map(platform => resolvePlatformType(platform, isFinOpsForAiEnabled))));
}

const PLATFORM_TYPE_LABEL_OVERRIDES: Partial<Record<Models.Platforms, string>> = {
    [Models.Platforms.ai]: "AI Services"
};

export function getPlatformTypeOptions(isFinOpsForAiEnabled: boolean = true): ControlOption<Models.Platforms>[]
{
    return UtilsService.getEnumValues<Models.Platforms>(Models.Platforms)
                       .filter(option => isFinOpsForAiEnabled || !isAiPlatform(option))
                       .map(option => new ControlOption(option, PLATFORM_TYPE_LABEL_OVERRIDES[option] ?? TitleCaseFormatPipe.format(option)));
}

export function getClusterTypeOptions(): ControlOption<string>[]
{
    return UtilsService.getEnumValues<ClusterType>(ClusterType)
                       .map(option => new ControlOption(option, UnderscoresForSpacesPipe.format(option)));
}

export const RESOURCE_SERVICE_LABEL_MAP: Lookup<string, Models.ResourceName> = {
    [Models.ResourceName.Anthropic]: "Anthropic"
};

const SAAS_SERVICE_LABEL_MAP: Lookup<string, ServiceProviderName> = {
    [Models.SaasServiceName.OpenAI]                : "OpenAI",
    [Models.SaasServiceName.Databricks]            : "Databricks",
    [Models.SaasServiceName.Snowflake]             : "Snowflake",
    [Models.SaasServiceName.Datadog]               : "Datadog",
    [Models.SaasServiceName.Splunk]                : "Splunk",
    [Models.SaasServiceName.CircleCI]              : "CircleCI",
    [Models.SaasServiceName.Elastic]               : "Elastic",
    [Models.SaasServiceName.Microsoft_Teams]       : "Microsoft Teams",
    [Models.SaasServiceName.Salesforce_Sales_Cloud]: "Salesforce Sales Cloud",
    [Models.SaasServiceName.Dropbox]               : "Dropbox",
    [Models.SaasServiceName.Okta]                  : "Okta",
    [Models.SaasServiceName.New_Relic]             : "New Relic",
    [Models.SaasServiceName.Dynatrace]             : "Dynatrace",
    [Models.SaasServiceName.Grafana]               : "Grafana",
    [Models.SaasServiceName.IBM_Tencent]           : "IBM (Tencent)",
    [Models.SaasServiceName.SaasCustomTool]        : "Other SaaS Tools"
};

export function getMultiCloudSaasOptions(): ControlOption<ServiceProviderName>[]
{
    const cloudServices: Models.CloudServiceName[] = [
        Models.CloudServiceName.AWS,
        Models.CloudServiceName.Azure,
        Models.CloudServiceName.GCP,
        Models.CloudServiceName.OCI
    ];

    const aiServices    = UtilsService.getEnumValues<Models.SaasAiServiceName>(Models.SaasAiServiceName);
    const dataServices  = UtilsService.getEnumValues<Models.SaasDataServiceName>(Models.SaasDataServiceName);
    const otherServices = UtilsService.getEnumValues<Models.SaasOtherServiceName>(Models.SaasOtherServiceName);

    const services: ServiceProviderName[] = [
        ...cloudServices,
        ...aiServices,
        ...dataServices,
        ...otherServices
    ];

    return services.map(service => new ControlOption(
        service,
        SAAS_SERVICE_LABEL_MAP[service] ?? service
    ));
}

export const DEFAULT_AI_SERVICES: Models.ResourceName[] = [Models.ResourceName.Anthropic];

export function getEffectiveAiServices(dashboard: { platform?: Models.Platforms[] | null; cloud?: Models.CloudServiceName | null; saas?: ServiceProviderName[] | null; aiServices?: string[] | null }): ServiceProviderName[] | null
{
    if (dashboard.aiServices != null) return dashboard.aiServices as ServiceProviderName[];

    if (dashboard.platform?.includes(Models.Platforms.ai)) return dashboard.saas ?? null;

    if (!dashboard.cloud && !dashboard.platform?.length) return [...DEFAULT_AI_SERVICES];

    return null;
}

export function getSelectedProviders(dashboard: { platform?: Models.Platforms[] | null; cloud?: Models.CloudServiceName | null; saas?: ServiceProviderName[] | null; aiServices?: string[] | null }): ServiceProviderName[] | null
{
    if (dashboard.platform?.includes(Models.Platforms.ai))
    {
        return getEffectiveAiServices(dashboard) ?? [];
    }
    return dashboard.saas ?? null;
}

export function getAllSelectedProviders(dashboard: { platform?: Models.Platforms[] | null; cloud?: Models.CloudServiceName | null; saas?: ServiceProviderName[] | null; aiServices?: string[] | null }): ServiceProviderName[]
{
    return [
        ...(dashboard.saas || []),
        ...(getEffectiveAiServices(dashboard) || [])
    ];
}

const AI_PLATFORM_CLOUD_PROVIDERS: Models.CloudServiceName[] = [
    Models.CloudServiceName.AWS,
    Models.CloudServiceName.Azure
];

export function getAiPlatformCloudProviderOptions(isFinOpsForAiAzureEnabled: boolean = true): ControlOption<ServiceProviderName>[]
{
    const aiResourceNames = UtilsService.getEnumValues<Models.ResourceName>(Models.ResourceName)
                                        .filter(resource => categorizeProvider(resource) === ProviderCategory.AI);

    const services: ServiceProviderName[] = [
        ...AI_PLATFORM_CLOUD_PROVIDERS.filter(cloud => isFinOpsForAiAzureEnabled || cloud !== Models.CloudServiceName.Azure),
        ...aiResourceNames
    ];

    return services.map(service => new ControlOption(
        service,
        RESOURCE_SERVICE_LABEL_MAP[service as Models.ResourceName] ?? SAAS_SERVICE_LABEL_MAP[service] ?? service
    ));
}

export enum PlatformScope
{
    MultiCloud          = "multi",
    SingleCloud         = "single",
    ContainerKubernetes = "container_kubernetes",
    ContainerAi         = "container_ai",
    MultiCloudSaas      = "multicloudsaas"
}

export function getTargetOptions(isTokenOps: boolean = false): ControlOption<PlatformScope>[]
{
    if (isTokenOps)
    {
        return [new ControlOption(PlatformScope.ContainerAi, "Platform Providers (AI Services)")];
    }

    return [
        new ControlOption(PlatformScope.MultiCloud, "Multi Cloud"),
        new ControlOption(PlatformScope.SingleCloud, "Single Cloud"),
        new ControlOption(PlatformScope.ContainerKubernetes, "Platform Providers (Kubernetes)"),
        new ControlOption(PlatformScope.MultiCloudSaas, "Multi Cloud SaaS")
    ];
}

export enum ProviderCategory
{
    Cloud         = "Cloud",
    AI            = "AI",
    DataServices  = "DataServices",
    OtherServices = "OtherServices"
}

export type ServiceProviderName = Models.CloudServiceName | Models.ResourceName | Models.SaasServiceName | Models.SaasAiServiceName | Models.SaasDataServiceName | Models.SaasOtherServiceName | Models.PolicyServiceName;

const RESOURCE_PROVIDER_CATEGORY_MAP: Lookup<ProviderCategory, Models.ResourceName> = {
    [Models.ResourceName.Anthropic]: ProviderCategory.AI
};

function buildProviderCategoryMap(): Lookup<ProviderCategory, ServiceProviderName>
{
    const map: Lookup<ProviderCategory, ServiceProviderName> = {};

    for (const cloudService of UtilsService.getEnumValues<Models.CloudServiceName>(Models.CloudServiceName))
    {
        map[cloudService] = ProviderCategory.Cloud;
    }

    for (const aiService of UtilsService.getEnumValues<Models.SaasAiServiceName>(Models.SaasAiServiceName))
    {
        map[aiService] = ProviderCategory.AI;
    }

    for (const resourceService of UtilsService.getEnumValues<Models.ResourceName>(Models.ResourceName))
    {
        const resourceCategory = RESOURCE_PROVIDER_CATEGORY_MAP[resourceService];
        if (resourceCategory) map[resourceService] = resourceCategory;
    }

    for (const dataService of UtilsService.getEnumValues<Models.SaasDataServiceName>(Models.SaasDataServiceName))
    {
        map[dataService] = ProviderCategory.DataServices;
    }

    for (const otherService of UtilsService.getEnumValues<Models.SaasOtherServiceName>(Models.SaasOtherServiceName))
    {
        map[otherService] = ProviderCategory.OtherServices;
    }

    return map;
}

export function isPlatformProvider(provider: ServiceProviderName): provider is Models.ResourceName
{
    return provider in Models.ResourceName;
}

export function resolveSinglePlatform(platforms: Models.Platforms[]): Models.Platforms | undefined
{
    return platforms?.length === 1 ? platforms[0] : undefined;
}

export function organizeProviders(providers: ServiceProviderName[]): [Models.CloudServiceName[], (Models.SaasServiceName | Models.ResourceName)[]]
{
    const organizedProviders: [Models.CloudServiceName[], (Models.SaasServiceName | Models.ResourceName)[]] = [
        [],
        []
    ];

    for (const provider of providers || [])
    {
        if (provider in Models.CloudServiceName)
        {
            organizedProviders[0].push(<Models.CloudServiceName>provider);
        }
        else if (provider in Models.SaasServiceName)
        {
            organizedProviders[1].push(<Models.SaasServiceName>provider);
        }
        else if (isPlatformProvider(provider))
        {
            organizedProviders[1].push(provider);
        }
    }

    return organizedProviders;
}

export const PROVIDER_CATEGORY_MAP = buildProviderCategoryMap();

export function categorizeProvider(provider: ServiceProviderName): ProviderCategory | null
{
    return PROVIDER_CATEGORY_MAP[provider] ?? null;
}

export function providerCategoryFriendlyName(category: ProviderCategory): string 
{
    switch (category) 
    {
        case ProviderCategory.Cloud:
            return "Cloud Service";

        case ProviderCategory.AI:
            return "AI Service";

        case ProviderCategory.DataServices:
            return "Data Service";
            
        case ProviderCategory.OtherServices:
            return "Other Service";
    }
}